import React from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { cellKey } from '../../../lib/canEndianness/bitMapping';
import { colorForIndex } from '../../../lib/canEndianness/defaults';

const BIT_LABELS = [0, 1, 2, 3, 4, 5, 6, 7];

// Renders the byte/bit grid as a table (rows = bytes, columns = Bit 0..Bit 7)
// and reports raw cell clicks upward — all selection-mode / anchor-tracking
// state lives in the orchestrator, since the "select bits for a new signal"
// workflow needs to share state with the signal-creation form.
const BitGrid = ({
  dlc,
  dataBytes,
  signals,
  cellOwnership,
  selectedSignalId,
  selectMode,
  pendingAnchor,
  onCellClick,
}) => {
  const signalIndexById = new Map(signals.map((s, i) => [s.id, i]));

  const cellInfo = (byteIndex, bitInByte) => {
    const owners = cellOwnership.get(cellKey(byteIndex, bitInByte)) || [];
    const bitValue = (dataBytes[byteIndex] >> bitInByte) & 1;
    return { owners, bitValue };
  };

  return (
    <div className="space-y-3">
      {selectMode && (
        <div className="text-xs font-mono bg-autoxium-blue/10 text-autoxium-blue rounded-md px-3 py-2" data-testid="can-endianness-select-mode-banner">
          {pendingAnchor
            ? 'Click a second bit to complete the selection…'
            : 'Select-bits mode: click a start bit, then an end bit, to derive Start Bit / Length automatically.'}
        </div>
      )}

      <Table data-testid="can-endianness-bit-grid">
        <TableHeader>
          <TableRow>
            <TableHead className="w-16 text-gray-400 font-mono text-xs">&nbsp;</TableHead>
            {BIT_LABELS.map((bitInByte) => (
              <TableHead key={bitInByte} className="text-center text-gray-400 font-mono text-xs w-10 sm:w-12 px-1">
                Bit {bitInByte}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: dlc }, (_, byteIndex) => (
            <TableRow key={byteIndex} data-testid={`can-endianness-byte-row-${byteIndex}`}>
              <TableCell className="font-mono text-xs font-bold whitespace-nowrap">Byte {byteIndex}</TableCell>
              {BIT_LABELS.map((bitInByte) => {
                const { owners, bitValue } = cellInfo(byteIndex, bitInByte);
                const isOverlap = owners.length > 1;
                const owner = owners[0];
                const isSelectedSignalCell = owner && owner.signalId === selectedSignalId;
                const isDimmed = selectedSignalId && owner && !isSelectedSignalCell;
                const isAnchor = pendingAnchor && pendingAnchor.byteIndex === byteIndex && pendingAnchor.bitInByte === bitInByte;

                let background = 'transparent';
                let textColor = 'inherit';
                if (isOverlap) {
                  background = 'repeating-linear-gradient(45deg, #dc2626, #dc2626 4px, #7f1d1d 4px, #7f1d1d 8px)';
                  textColor = '#fff';
                } else if (owner) {
                  const color = colorForIndex(signalIndexById.get(owner.signalId) ?? 0);
                  background = isDimmed ? `${color}30` : color;
                  textColor = isDimmed ? 'inherit' : '#fff';
                }

                const ownerSignal = owner ? signals.find((s) => s.id === owner.signalId) : null;
                const title = ownerSignal
                  ? `Byte ${byteIndex}, Bit ${bitInByte} — ${ownerSignal.name || 'Signal'} (bit ${owner.positionIndex + 1} of ${ownerSignal.length}) — value ${bitValue}`
                  : `Byte ${byteIndex}, Bit ${bitInByte} — Unused — value ${bitValue}`;

                return (
                  <TableCell key={bitInByte} className="p-1 text-center">
                    <button
                      type="button"
                      title={title}
                      onClick={() => onCellClick(byteIndex, bitInByte, owner ? owner.signalId : null)}
                      className={`h-8 w-8 sm:h-9 sm:w-9 mx-auto flex items-center justify-center text-xs font-mono rounded border border-structure/60 transition-colors ${
                        selectMode ? 'cursor-crosshair hover:brightness-110' : owner ? 'cursor-pointer hover:brightness-110' : 'cursor-default'
                      } ${isAnchor ? 'ring-2 ring-inset ring-autoxium-blue' : ''}`}
                      style={{ background, color: textColor }}
                      data-testid={`can-endianness-cell-${byteIndex}-${bitInByte}`}
                    >
                      {bitValue}
                    </button>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {signals.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1" data-testid="can-endianness-legend">
          {signals.map((s, i) => (
            <div key={s.id} className="flex items-center gap-1.5 text-xs">
              <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: colorForIndex(i) }} />
              <span className={selectedSignalId === s.id ? 'font-bold' : 'text-muted-foreground'}>{s.name || 'Unnamed'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BitGrid;
